import { FC } from "react";
import styles from "./FormationBoard.module.scss";

interface LineupPosition {
  athleteId: string;
  positionId?: string;
  shirtNumber?: number;
  roleLabel?: string;
  customPosition?: { x: number; y: number };
  name?: string;
  riScore?: number;
}

interface FormationBoardProps {
  sport: "soccer" | "rugby" | "basket" | "volleyball" | "futsal";
  startingLineup: LineupPosition[];
  onPlayerClick?: (athleteId: string) => void;
  selectedAthleteId?: string;
}

/**
 * SVG campo adattivo per sport + posizioni giocatori droppable.
 * Spec: LEOPART_ProgrammazioneCampo_Spec_v1.1.1.md righe 684-713.
 * SVG semplice — nessuna libreria drag-drop esterna (useMemo + state per override posizione).
 */
const FIELD_CONFIG = {
  soccer: { label: "Calcio 11", viewBox: "0 0 340 520", playerCount: 11 },
  rugby: { label: "Rugby 15", viewBox: "0 0 340 520", playerCount: 15 },
  basket: { label: "Basket 5", viewBox: "0 0 340 300", playerCount: 5 },
  volleyball: { label: "Pallavolo 6", viewBox: "0 0 340 260", playerCount: 6 },
  futsal: { label: "Futsal 5", viewBox: "0 0 340 260", playerCount: 5 },
};

/** Renderizza il campo SVG appropriato in base allo sport */
const FieldSvg: FC<{ sport: FormationBoardProps["sport"] }> = ({ sport }) => {
  if (sport === "basket") {
    return (
      <>
        <rect x="10" y="10" width="320" height="280" rx="4" fill="#1a6b32" stroke="#ffffff" strokeWidth="2" strokeOpacity="0.5" />
        <line x1="10" y1="150" x2="330" y2="150" stroke="#ffffff" strokeWidth="1.5" strokeOpacity="0.6" />
        <circle cx="170" cy="150" r="30" fill="none" stroke="#ffffff" strokeWidth="1.5" strokeOpacity="0.6" />
        <rect x="90" y="10" width="160" height="70" fill="none" stroke="#ffffff" strokeWidth="1.5" strokeOpacity="0.6" />
        <rect x="90" y="220" width="160" height="70" fill="none" stroke="#ffffff" strokeWidth="1.5" strokeOpacity="0.6" />
        <circle cx="170" cy="10" r="4" fill="#ffffff" fillOpacity="0.6" />
        <circle cx="170" cy="290" r="4" fill="#ffffff" fillOpacity="0.6" />
      </>
    );
  }

  if (sport === "volleyball") {
    return (
      <>
        <rect x="10" y="10" width="320" height="240" rx="4" fill="#2a4a8a" stroke="#ffffff" strokeWidth="2" strokeOpacity="0.5" />
        <line x1="10" y1="130" x2="330" y2="130" stroke="#ffd15a" strokeWidth="3" strokeOpacity="0.8" />
        <line x1="10" y1="10" x2="330" y2="10" stroke="#ffffff" strokeWidth="1" strokeOpacity="0.4" />
        <line x1="10" y1="250" x2="330" y2="250" stroke="#ffffff" strokeWidth="1" strokeOpacity="0.4" />
      </>
    );
  }

  if (sport === "rugby") {
    return (
      <>
        <rect x="10" y="10" width="320" height="500" rx="4" fill="#1a6b32" stroke="#ffffff" strokeWidth="2" strokeOpacity="0.5" />
        {/* In-goal areas */}
        <rect x="10" y="10" width="320" height="55" fill="#15592a" stroke="#ffffff" strokeWidth="1.5" strokeOpacity="0.6" />
        <rect x="10" y="455" width="320" height="55" fill="#15592a" stroke="#ffffff" strokeWidth="1.5" strokeOpacity="0.6" />
        {/* 10m lines */}
        <line x1="10" y1="155" x2="330" y2="155" stroke="#ffffff" strokeWidth="1" strokeOpacity="0.5" />
        <line x1="10" y1="365" x2="330" y2="365" stroke="#ffffff" strokeWidth="1" strokeOpacity="0.5" />
        {/* Halfway */}
        <line x1="10" y1="260" x2="330" y2="260" stroke="#ffffff" strokeWidth="2" strokeOpacity="0.7" />
        {/* 22m lines */}
        <line x1="10" y1="200" x2="330" y2="200" stroke="#ffffff" strokeWidth="1" strokeOpacity="0.4" />
        <line x1="10" y1="320" x2="330" y2="320" stroke="#ffffff" strokeWidth="1" strokeOpacity="0.4" />
      </>
    );
  }

  // Soccer / Futsal default
  return (
    <>
      <rect x="10" y="10" width="320" height="500" rx="4" fill="#1a6b32" stroke="#ffffff" strokeWidth="2" strokeOpacity="0.5" />
      {/* Halfway line */}
      <line x1="10" y1="260" x2="330" y2="260" stroke="#ffffff" strokeWidth="1.5" strokeOpacity="0.7" />
      <circle cx="170" cy="260" r="40" fill="none" stroke="#ffffff" strokeWidth="1.5" strokeOpacity="0.6" />
      <circle cx="170" cy="260" r="3" fill="#ffffff" fillOpacity="0.8" />
      {/* Top penalty area */}
      <rect x="60" y="10" width="220" height="100" fill="none" stroke="#ffffff" strokeWidth="1.5" strokeOpacity="0.6" />
      <rect x="110" y="10" width="120" height="50" fill="none" stroke="#ffffff" strokeWidth="1.5" strokeOpacity="0.6" />
      <circle cx="170" cy="100" r="3" fill="#ffffff" fillOpacity="0.8" />
      {/* Bottom penalty area */}
      <rect x="60" y="410" width="220" height="100" fill="none" stroke="#ffffff" strokeWidth="1.5" strokeOpacity="0.6" />
      <rect x="110" y="460" width="120" height="50" fill="none" stroke="#ffffff" strokeWidth="1.5" strokeOpacity="0.6" />
      <circle cx="170" cy="420" r="3" fill="#ffffff" fillOpacity="0.8" />
    </>
  );
};

const FormationBoard: FC<FormationBoardProps> = ({
  sport,
  startingLineup,
  onPlayerClick,
  selectedAthleteId,
}) => {
  const config = FIELD_CONFIG[sport] ?? FIELD_CONFIG.soccer;

  return (
    <div className={styles.boardWrap}>
      <svg
        viewBox={config.viewBox}
        className={styles.fieldSvg}
        xmlns="http://www.w3.org/2000/svg"
      >
        <FieldSvg sport={sport} />

        {/* Overlay giocatori sulle posizioni */}
        {startingLineup.map((player, idx) => {
          const pos = player.customPosition;
          if (!pos) return null;
          const cx = pos.x;
          const cy = pos.y;
          const isSelected = player.athleteId === selectedAthleteId;

          return (
            <g
              key={player.athleteId}
              onClick={() => onPlayerClick?.(player.athleteId)}
              style={{ cursor: "pointer" }}
            >
              <circle
                cx={cx}
                cy={cy}
                r={18}
                fill={isSelected ? "#ffd15a" : "#063444"}
                stroke={isSelected ? "#00212c" : "#ffffff"}
                strokeWidth="2"
                opacity="0.95"
              />
              <text
                x={cx}
                y={cy - 2}
                textAnchor="middle"
                dominantBaseline="middle"
                fill="#ffffff"
                fontSize="10"
                fontFamily="Satoshi, sans-serif"
                fontWeight="700"
              >
                {player.shirtNumber ?? idx + 1}
              </text>
              <text
                x={cx}
                y={cy + 26}
                textAnchor="middle"
                fill="#ffffff"
                fontSize="8"
                fontFamily="Satoshi, sans-serif"
                fontWeight="700"
                opacity="0.9"
              >
                {player.name?.split(" ").pop()?.toUpperCase() ?? ""}
              </text>
            </g>
          );
        })}

        {/* Label sport */}
        <text
          x="170"
          y="8"
          textAnchor="middle"
          fill="#ffffff"
          fontSize="8"
          fontFamily="Satoshi, sans-serif"
          opacity="0.4"
        >
          {config.label}
        </text>
      </svg>

      {/* Fallback: nessuna posizione custom → mostra lista */}
      {startingLineup.every((p) => !p.customPosition) && (
        <div className={styles.noPositionHint}>
          <p className={styles.hintText}>
            Usa "Salva formazione" per posizionare i giocatori sul campo
          </p>
        </div>
      )}
    </div>
  );
};

export default FormationBoard;
